/*
  KR Motion — homepage scroll animations for the revamp sections (GSAP + ScrollTrigger).
  Loaded on the homepage only (layout/theme.liquid), after gsap.min.js and gsap-scrolltrigger.min.js.

  Rules:
  - Content is visible without this file; elements are only hidden right before their reveal is armed
  - Anything already on screen when the page loads is left alone (no flash on refresh mid-page)
  - Transform + opacity only, every reveal plays once
  - Skipped for prefers-reduced-motion and inside the theme editor
  - Scroll-linked parallax runs on desktop (750px+) only

  Sections are found by their existing class names, so no section markup depends on this file.
*/
(() => {
  const { gsap, ScrollTrigger } = window;
  if (!gsap || !ScrollTrigger) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  gsap.registerPlugin(ScrollTrigger);

  const EASE = 'power3.out';
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const onScreen = (el) => el.getBoundingClientRect().top < window.innerHeight;

  // Fade-up a group once when its trigger scrolls in; skipped if already visible at load
  const reveal = (targets, { trigger, y = 32, stagger = 0.08, delay = 0, duration = 0.9, start = 'top 82%' } = {}) => {
    const els = (Array.isArray(targets) ? targets : qsa(targets)).filter(Boolean);
    if (!els.length) return null;
    const anchor = trigger || els[0];
    if (onScreen(anchor)) return null;
    gsap.set(els, { autoAlpha: 0, y });
    return gsap.to(els, {
      autoAlpha: 1,
      y: 0,
      duration,
      delay,
      stagger,
      ease: EASE,
      clearProps: 'transform,opacity,visibility',
      scrollTrigger: { trigger: anchor, start, once: true },
    });
  };

  // Horizontal scrollers: slide the track in from the right, then a small "peek" so it reads as swipeable
  const slideAndPeek = (track, items) => {
    if (!track || onScreen(track)) return;
    const scrollable = track.scrollWidth > track.clientWidth + 8;
    gsap.set(items, { autoAlpha: 0 });
    gsap.set(track, { x: 96 });
    const tl = gsap.timeline({ scrollTrigger: { trigger: track, start: 'top 80%', once: true } });
    tl.to(track, { x: 0, duration: 1.1, ease: EASE }, 0)
      .to(items, { autoAlpha: 1, duration: 0.7, stagger: 0.08, ease: 'power1.out', clearProps: 'opacity,visibility' }, 0);
    if (scrollable) {
      tl.to(track, { x: -48, duration: 0.45, ease: 'power2.inOut' }, '+=0.25')
        .to(track, { x: 0, duration: 0.6, ease: 'power2.inOut' });
    }
    tl.set(track, { clearProps: 'transform' });
  };

  const mm = gsap.matchMedia();

  /* Hero 2 — image settles from its pre-zoom (set in theme.liquid <head>); desktop parallax on scroll */
  qsa('.ds-hero2').forEach((hero) => {
    const img = hero.querySelector('.ds-hero2__media img, .ds-hero2__media svg');
    if (!img) return;
    gsap.fromTo(img, { scale: 1.06 }, { scale: 1, duration: 1.6, ease: 'power2.out' });
    mm.add('(min-width: 750px)', () => {
      // Image drifts down slower than the page; the gap it opens at the top is always above the viewport
      gsap.to(img, {
        yPercent: 12,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
      });
    });
  });

  /* Card grid — header, then cards */
  qsa('.kr-grid').forEach((grid) => {
    reveal(qsa('.kr-grid__header > *', grid), { trigger: grid, stagger: 0.1 });
    reveal(qsa('.kr-grid__list > li', grid), { trigger: grid.querySelector('.kr-grid__list'), y: 40 });
  });

  /* Hover story — text, main image wipes up, then thumbnails */
  qsa('.kr-story').forEach((story) => {
    const feature = story.querySelector('.kr-story__feature');
    reveal(qsa('.kr-story__text', story), { trigger: story });
    if (feature && !onScreen(feature)) {
      gsap.fromTo(
        feature,
        { clipPath: 'inset(100% 0% 0% 0% round 16px)' },
        {
          clipPath: 'inset(0% 0% 0% 0% round 16px)',
          duration: 1.2,
          ease: 'power3.inOut',
          clearProps: 'clipPath',
          scrollTrigger: { trigger: feature, start: 'top 85%', once: true },
        }
      );
    }
    reveal(qsa('.kr-story__list > li', story), { trigger: story.querySelector('.kr-story__list'), delay: 0.3, stagger: 0.1 });
  });

  /* Science study — copy rises, stat numbers count up, callouts pop in one by one */
  qsa('.kr-science').forEach((science) => {
    reveal(qsa('.kr-science__copy > *', science), { trigger: science.querySelector('.kr-science__panel'), stagger: 0.1 });

    qsa('.kr-science__stat-value', science).forEach((value) => {
      // The number is the last text node (after the optional arrow + visually-hidden label)
      const node = Array.from(value.childNodes).reverse().find((n) => n.nodeType === 3 && n.textContent.trim());
      const match = node && node.textContent.match(/^(\s*[^\d-]*)(-?[\d,]*\.?\d+)(.*)$/s);
      if (!match || onScreen(value)) return;
      const [, prefix, raw, suffix] = match;
      const target = parseFloat(raw.replace(/,/g, ''));
      const decimals = (raw.split('.')[1] || '').length;
      const format = (n) =>
        n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: raw.includes(',') });
      const counter = { n: 0 };
      value.style.fontVariantNumeric = 'tabular-nums';
      node.textContent = prefix + format(0) + suffix;
      gsap.to(counter, {
        n: target,
        duration: 1.8,
        ease: 'power2.out',
        onUpdate: () => { node.textContent = prefix + format(counter.n) + suffix; },
        onComplete: () => { node.textContent = prefix + raw + suffix; },
        scrollTrigger: { trigger: value, start: 'top 90%', once: true },
      });
    });

    const callouts = qsa('.kr-science__callout', science);
    if (callouts.length && !onScreen(callouts[0])) {
      gsap.set(callouts, { autoAlpha: 0, scale: 0.92, y: 12 });
      gsap.to(callouts, {
        autoAlpha: 1,
        scale: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.18,
        delay: 0.4,
        ease: 'back.out(1.6)',
        clearProps: 'transform,opacity,visibility',
        scrollTrigger: { trigger: science.querySelector('.kr-science__visual'), start: 'top 75%', once: true },
      });
    }
  });

  /* Autoplay — text staggers in; video tile scales up to full size as it scrolls into view */
  qsa('.kr-auto').forEach((auto) => {
    reveal(qsa('.kr-auto__eyebrow, .kr-auto__content > *, .kr-auto__label', auto), { trigger: auto, stagger: 0.1 });
    const media = auto.querySelector('.kr-auto__media');
    if (!media || onScreen(media)) return;
    mm.add({ desktop: '(min-width: 750px)', mobile: '(max-width: 749px)' }, (ctx) => {
      gsap.fromTo(
        media,
        { scale: ctx.conditions.desktop ? 0.86 : 0.94 },
        { scale: 1, ease: 'none', scrollTrigger: { trigger: media, start: 'top bottom', end: 'center center', scrub: 0.6 } }
      );
    });
  });

  /* Video panels — heading, then the track slides in and peeks */
  qsa('.kr-panels').forEach((panels) => {
    reveal(qsa('.kr-panels__head > *', panels), { trigger: panels, stagger: 0.1 });
    slideAndPeek(panels.querySelector('.kr-panels__track'), qsa('.kr-panels__panel', panels));
  });

  /* Lifestyle reviews — heading, track slides in and peeks, stars pop in left to right */
  qsa('.kr-lr').forEach((lr) => {
    reveal(qsa('.kr-lr__head > *', lr), { trigger: lr, stagger: 0.1 });
    slideAndPeek(lr.querySelector('.kr-lr__track'), qsa('.kr-lr__tile', lr));
    qsa('.kr-lr__card .kr-lr__stars', lr).forEach((stars) => {
      const icons = qsa('svg', stars);
      if (!icons.length || onScreen(stars)) return;
      gsap.set(icons, { scale: 0, transformOrigin: '50% 50%' });
      gsap.to(icons, {
        scale: 1,
        duration: 0.45,
        stagger: 0.07,
        delay: 0.5,
        ease: 'back.out(2.2)',
        clearProps: 'transform',
        scrollTrigger: { trigger: stars, start: 'top 90%', once: true },
      });
    });
  });

  /* CTA blocks — tile copy rises; photos drift inside their tiles on desktop */
  qsa('.kr-cta').forEach((cta) => {
    qsa('.kr-cta__tile', cta).forEach((tile, i) => {
      reveal(qsa('.kr-cta__heading, .kr-cta__text, .kr-cta__button', tile), { trigger: tile, delay: (i % 2) * 0.12 });
    });
    mm.add('(min-width: 750px)', () => {
      // Parallax moves the media wrapper, so the image's own hover zoom keeps working
      qsa('.kr-cta__media', cta).forEach((media) => {
        gsap.fromTo(
          media,
          { scale: 1.16, yPercent: -6 },
          {
            yPercent: 6,
            ease: 'none',
            scrollTrigger: { trigger: media.closest('.kr-cta__tile'), start: 'top bottom', end: 'bottom top', scrub: true },
          }
        );
      });
    });
  });

  // Lazy images and fonts change heights after load; re-measure trigger positions once everything settles
  window.addEventListener('load', () => ScrollTrigger.refresh());
})();
